import { type State } from 'src/types';

import { Urls } from '../../constants/Urls';

import { createHelpUrl, getCurrentPathnameFromState, getSubscriptionPath } from './shared';

export function isOnPlansPage(state: State): boolean {
  const currentPath = getCurrentPathnameFromState(state);
  return currentPath === '/plans';
}

export function getFamilyPlanManagementPath() {
  return '/family/invite' as const;
}

export function getFamilyPlanInviteeLandingPath() {
  return '/family/accept' as const;
}

export function getPartnerUpgradePath() {
  return '/subscription/activate' as const;
}

export function isPartnerUpgradePath(path: string): boolean {
  return /subscription\/activate.*/.test(path);
}

export function getAttPremiumPath() {
  return '/subscription/premium-att' as const;
}

export function isAttPremiumPath(path: string): boolean {
  return /subscription\/premium-att.*/.test(path);
}

export function isOfferPath(path: string): boolean {
  return path.includes('/offer');
}

export function isOnSubscriptionPage(state: State): boolean {
  const path = getCurrentPathnameFromState(state);
  return path?.includes(getSubscriptionPath());
}

export function getPremiumMicrositePath() {
  return '/premium' as const;
}

export function getPremiumInvitePath() {
  return '/premium/invite' as const;
}

export function getPremiumWelcomePath() {
  return '/premium/welcome' as const;
}

export function getPlusUpgradePath() {
  return '/upgrade/plus' as const;
}

export function getPremiumUpgradePath() {
  return '/upgrade/premium' as const;
}

export function getFamilyPlanUpgradePath() {
  return '/upgrade/premium/family-plan' as const;
}

export function getPremiumMilitaryPlanUpgradePath() {
  return '/upgrade/premium/military' as const;
}

export function getPremiumStudentPlanUpgradePath() {
  return '/upgrade/premium/student' as const;
}

export function getUpgradeLandingPath() {
  return '/upgrade' as const;
}

export function getPlansPath() {
  return '/plans' as const;
}

export function getTermsUrl() {
  return `${Urls.SUBSCRIPTION_TERMS}` as const;
}

export function getTrialOffersTermsUrl() {
  return `${Urls.TRIAL_OFFERS_TERMS}` as const;
}

export function getChangePaymentHelpUrl() {
  return createHelpUrl('/s/article/Change-your-Payment-Information-1519949295577');
}

export function getContactSupportUrl() {
  return createHelpUrl('/s/contactsupport');
}

export function getCancelVBSHelpUrl() {
  return createHelpUrl('/s/article/Cancel-your-Subscription-1519949295562');
}

export function getUpgradePath<TierSlug extends string, SubTierSlug extends string>(
  tierSlug?: TierSlug,
  subTierSlug?: SubTierSlug,
): string {
  if (tierSlug && subTierSlug) {
    return `/upgrade/${tierSlug}/${subTierSlug}` as `/upgrade/${TierSlug}/${SubTierSlug}`;
  } else if (tierSlug) {
    return `/upgrade/${tierSlug}` as `/upgrade/${TierSlug}`;
  }

  return '/upgrade' as const;
}

export function isFamilyPlanPath(path: string): boolean {
  return /^\/family(\/(manage|accept|invite))?$/.test(path);
}

export function isUpgradeOrSettingsUpgradePage(path: string): boolean {
  return path?.includes(getUpgradePath());
}

export function isOnUpgradePage(state: State): boolean {
  const currentPath = getCurrentPathnameFromState(state);
  return isUpgradeOrSettingsUpgradePage(currentPath);
}

export function isPaymentPath(path: string): boolean {
  return /^\/payment(.*)$/.test(path);
}

export function isOnPaymentsPage(state: State): boolean {
  const currentPath = getCurrentPathnameFromState(state);
  return isPaymentPath(currentPath);
}

export function isOnUpgradeOrPaymentsPage(state: State): boolean {
  return isOnUpgradePage(state) || isOnPaymentsPage(state);
}

// TODO: Temp solution, remove get(.*)PaymentPath in WEBCLIENT-7008
export function getPlusPaymentPath() {
  return '/payment/plus' as const;
}

export function getPremiumPaymentPath() {
  return '/payment/premium' as const;
}

export function getFamilyPlanPaymentPath() {
  return '/payment/premium/family-plan' as const;
}

export function getPremiumMilitaryPlanPaymentPath() {
  return '/payment/premium/military' as const;
}

export function getPremiumStudentPlanPaymentPath() {
  return '/payment/premium/student' as const;
}

export function getPremiumStudentPlanVerificationPath() {
  return '/verify/premium/student' as const;
}

export function getPremiumMilitaryPlanVerificationPath() {
  return '/verify/premium/military' as const;
}

export function isUpgradeLandingPath(state: State): boolean {
  const currentPath = getCurrentPathnameFromState(state);
  return currentPath === getUpgradeLandingPath();
}

export function getStudentTermsUrl() {
  return `${Urls.STUDENT_TERMS}` as const;
}

export function getMilitaryTermsUrl() {
  return `${Urls.MILITARY_TERMS}` as const;
}

export function getPaymentPath<TierSlug extends string, SubTierSlug extends string>(
  tierSlug?: TierSlug,
  subTierSlug?: SubTierSlug,
): string {
  if (tierSlug && subTierSlug) {
    return `/payment/${tierSlug}/${subTierSlug}` as `/payment/${TierSlug}/${SubTierSlug}`;
  } else if (tierSlug) {
    return `/payment/${tierSlug}` as `/payment/${TierSlug}`;
  }

  return '';
}

export function getDiscountVerificationPath(tierSlug?: string, verificationType?: string): string {
  if (tierSlug && verificationType) {
    return `/verify/${tierSlug}/${verificationType}` as const;
  }
  return '';
}

export function isOnVerificationPage(state: State): boolean {
  const currentPath = getCurrentPathnameFromState(state);
  return /^\/verify(.*)$/.test(currentPath);
}

export function getDeepLinkAccountBilling() {
  return 'pandorav5://account/billing' as const;
}

export function getAccountBillingUniversalLinkPathname() {
  return '/account/billing' as const;
}

export function getCongratulationsPath() {
  return '/settings/redeem/congratulations' as const;
}

export function getStudentReverificationCongratulationsPath() {
  return '/verify/premium/student/congratulations' as const;
}

export function getCancelUrl() {
  return Urls.CANCEL;
}
